import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/it";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, authFetch, useAuth } from "@/App";
import { 
  ArrowLeft, Syringe, Bell, Stethoscope, Calendar as CalendarIcon,
  ChevronLeft, ChevronRight, PawPrint, Check, Clock, AlertTriangle, X
} from "lucide-react";

moment.locale('it');

// Event colors
const EVENT_CONFIG = {
  vaccine: { label: "Vaccini", icon: Syringe, color: "#EF4444", bg: "bg-red-500" },
  reminder: { label: "Promemoria", icon: Bell, color: "#F59E0B", bg: "bg-amber-500" },
  appointment: { label: "Appuntamenti", icon: Stethoscope, color: "#3B82F6", bg: "bg-blue-500" }
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [pets, setPets] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedPet, setSelectedPet] = useState("all");
  const [activeFilters, setActiveFilters] = useState(["vaccine", "reminder", "appointment"]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [summary, setSummary] = useState({ total: 0, vaccines: 0, reminders: 0, appointments: 0 });

  useEffect(() => {
    if (user) {
      fetchPets();
      fetchEvents();
    }
  }, [user, currentDate, selectedPet]);

  const fetchPets = async () => {
    try {
      const res = await authFetch(`${API}/pets`);
      if (res.ok) setPets(await res.json());
    } catch (error) {
      console.error("Error fetching pets:", error);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const start = moment(currentDate).subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
      const end = moment(currentDate).add(2, 'months').endOf('month').format('YYYY-MM-DD');
      
      let url = `${API}/calendar/events?start_date=${start}&end_date=${end}`;
      if (selectedPet !== "all") url += `&pet_id=${selectedPet}`;
      
      const res = await authFetch(url);
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
        setSummary(data.summary || { total: 0, vaccines: 0, reminders: 0, appointments: 0 });
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  // Filter events
  const filteredEvents = useMemo(() => {
    return events.filter(e => activeFilters.includes(e.type));
  }, [events, activeFilters]);

  // Group events by date for the month view
  const eventsByDate = useMemo(() => {
    const map = {};
    filteredEvents.forEach(event => {
      const dateKey = event.date.split('T')[0];
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(event);
    });
    return map;
  }, [filteredEvents]);

  // Get days in current month
  const daysInMonth = useMemo(() => {
    const start = moment(currentDate).startOf('month');
    const end = moment(currentDate).endOf('month');
    const startWeekday = start.day(); // 0 = Sunday
    const days = [];
    
    // Add empty slots for days before start of month
    for (let i = 0; i < startWeekday; i++) {
      days.push({ date: null, empty: true });
    }
    
    // Add days of month
    let current = start.clone();
    while (current <= end) {
      days.push({
        date: current.format('YYYY-MM-DD'),
        day: current.date(),
        isToday: current.isSame(moment(), 'day')
      });
      current.add(1, 'day');
    }
    
    return days;
  }, [currentDate]);

  // Navigation
  const goToPrevMonth = () => setCurrentDate(moment(currentDate).subtract(1, 'month').toDate());
  const goToNextMonth = () => setCurrentDate(moment(currentDate).add(1, 'month').toDate());
  const goToToday = () => setCurrentDate(new Date());

  // Toggle filter
  const toggleFilter = (type) => {
    if (activeFilters.includes(type)) {
      if (activeFilters.length > 1) setActiveFilters(activeFilters.filter(f => f !== type));
    } else {
      setActiveFilters([...activeFilters, type]);
    }
  };

  // Upcoming events (next 14 days)
  const upcomingEvents = useMemo(() => {
    const today = new Date();
    const twoWeeks = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
    
    return filteredEvents
      .filter(e => {
        const eventDate = new Date(e.date);
        return eventDate >= today && eventDate <= twoWeeks;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 8);
  }, [filteredEvents]);

  // Complete reminder
  const handleCompleteReminder = async (reminderId) => {
    try {
      const res = await authFetch(`${API}/reminders/${reminderId}/complete`, { method: 'POST' });
      if (res.ok) {
        toast.success("Promemoria completato!");
        setSelectedEvent(null);
        fetchEvents();
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A] pb-24 lg:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 dark:bg-[#1A1A1A]/90 backdrop-blur-xl border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">Calendario</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {summary.total} eventi in programma
                </p>
              </div>
            </div>
            
            {/* Pet filter */}
            <select
              value={selectedPet}
              onChange={(e) => setSelectedPet(e.target.value)}
              className="text-sm bg-gray-100 dark:bg-gray-800 border-0 rounded-lg px-3 py-1.5 text-gray-700 dark:text-gray-300"
            >
              <option value="all">Tutti</option>
              {pets.map(pet => (
                <option key={pet.pet_id} value={pet.pet_id}>{pet.name}</option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4">
        {/* Filters - Clean pills */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
          {Object.entries(EVENT_CONFIG).map(([type, config]) => {
            const Icon = config.icon;
            const isActive = activeFilters.includes(type);
            const count = type === 'vaccine' ? summary.vaccines : type === 'reminder' ? summary.reminders : summary.appointments;
            
            return (
              <button
                key={type}
                onClick={() => toggleFilter(type)}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  isActive 
                    ? `${config.bg} text-white shadow-md` 
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                }`}
              >
                <Icon size={14} />
                {config.label}
                {count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-white dark:bg-[#1D1D1F] rounded-2xl shadow-sm overflow-hidden">
            {/* Month navigation */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
              <Button variant="ghost" size="icon" onClick={goToPrevMonth} className="rounded-full">
                <ChevronLeft size={20} />
              </Button>
              <div className="text-center">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white capitalize">
                  {moment(currentDate).format('MMMM YYYY')}
                </h2>
                <button onClick={goToToday} className="text-xs text-blue-500 hover:underline">
                  Oggi
                </button>
              </div>
              <Button variant="ghost" size="icon" onClick={goToNextMonth} className="rounded-full">
                <ChevronRight size={20} />
              </Button>
            </div>
            
            {/* Days header */}
            <div className="grid grid-cols-7 text-center py-2 border-b border-gray-100 dark:border-gray-800">
              {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map(day => (
                <div key={day} className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Days grid */}
            <div className="grid grid-cols-7 gap-px bg-gray-100 dark:bg-gray-800">
              {daysInMonth.map((day, idx) => {
                const dayEvents = day.date ? eventsByDate[day.date] || [] : [];
                const hasVaccine = dayEvents.some(e => e.type === 'vaccine');
                const hasReminder = dayEvents.some(e => e.type === 'reminder');
                const hasAppointment = dayEvents.some(e => e.type === 'appointment');
                
                return (
                  <div
                    key={idx}
                    className={`min-h-[70px] sm:min-h-[80px] p-1 sm:p-2 bg-white dark:bg-[#1D1D1F] ${
                      day.empty ? 'opacity-30' : ''
                    } ${day.isToday ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                  >
                    {!day.empty && (
                      <>
                        <span className={`text-sm font-medium ${
                          day.isToday 
                            ? 'text-blue-600 dark:text-blue-400' 
                            : 'text-gray-700 dark:text-gray-300'
                        }`}>
                          {day.day}
                        </span>
                        
                        {/* Event dots */}
                        {dayEvents.length > 0 && (
                          <div className="flex gap-0.5 mt-1 flex-wrap">
                            {hasVaccine && <div className="w-2 h-2 rounded-full bg-red-500" />}
                            {hasReminder && <div className="w-2 h-2 rounded-full bg-amber-500" />}
                            {hasAppointment && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                          </div>
                        )}
                        
                        {/* Event previews (desktop only) */}
                        <div className="hidden sm:block mt-1 space-y-0.5">
                          {dayEvents.slice(0, 2).map((event, i) => (
                            <button
                              key={i}
                              onClick={() => setSelectedEvent(event)}
                              className={`w-full text-left text-xs px-1 py-0.5 rounded truncate ${
                                event.type === 'vaccine' ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300' :
                                event.type === 'reminder' ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300' :
                                'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300'
                              }`}
                            >
                              {event.title}
                            </button>
                          ))}
                          {dayEvents.length > 2 && (
                            <span className="text-xs text-gray-500">+{dayEvents.length - 2}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="bg-white dark:bg-[#1D1D1F] rounded-2xl shadow-sm p-4">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Clock size={18} className="text-blue-500" />
              Prossimi eventi
            </h3>
            
            {upcomingEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <CalendarIcon className="mx-auto mb-2 opacity-50" size={32} />
                <p className="text-sm">Nessun evento nei prossimi 14 giorni</p>
              </div>
            ) : (
              <div className="space-y-2">
                {upcomingEvents.map(event => {
                  const config = EVENT_CONFIG[event.type];
                  const Icon = config?.icon || Bell;
                  const isToday = moment(event.date).isSame(moment(), 'day');
                  const isTomorrow = moment(event.date).isSame(moment().add(1, 'day'), 'day');
                  
                  return (
                    <button
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                    >
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${config?.color}20` }}
                      >
                        <Icon size={18} style={{ color: config?.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-800 dark:text-gray-200 truncate">
                          {event.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {event.pet_name} • {isToday ? 'Oggi' : isTomorrow ? 'Domani' : moment(event.date).format('D MMM')}
                        </p>
                      </div>
                      {isToday && (
                        <span className="text-xs bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-full">
                          Oggi
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Event detail dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-sm rounded-2xl">
          {selectedEvent && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${EVENT_CONFIG[selectedEvent.type]?.color}20` }}
                  >
                    {(() => {
                      const Icon = EVENT_CONFIG[selectedEvent.type]?.icon || Bell;
                      return <Icon size={24} style={{ color: EVENT_CONFIG[selectedEvent.type]?.color }} />;
                    })()}
                  </div>
                  <div>
                    <DialogTitle className="text-base">{selectedEvent.title}</DialogTitle>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{selectedEvent.pet_name}</p>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-3 mt-2">
                <div className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                  <CalendarIcon size={16} className="text-gray-400" />
                  {moment(selectedEvent.date).format('dddd D MMMM YYYY')}
                </div>
                
                {selectedEvent.time && (
                  <div className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <Clock size={16} className="text-gray-400" />
                    {selectedEvent.time}
                  </div>
                )}
                
                {/* Type specific details */}
                {selectedEvent.details && (
                  <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800 text-sm space-y-1">
                    {selectedEvent.details.vaccine_name && (
                      <p><span className="text-gray-500">Vaccino:</span> {selectedEvent.details.vaccine_name}</p>
                    )}
                    {selectedEvent.details.vet_name && (
                      <p><span className="text-gray-500">Veterinario:</span> {selectedEvent.details.vet_name}</p>
                    )}
                    {selectedEvent.details.notes && (
                      <p><span className="text-gray-500">Note:</span> {selectedEvent.details.notes}</p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 mt-4">
                {selectedEvent.type === "reminder" && (
                  <Button
                    onClick={() => handleCompleteReminder(selectedEvent.id)}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                  >
                    <Check size={16} className="mr-2" />
                    Completato
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate(`/fascicolo/${selectedEvent.pet_id}`)}
                  className="flex-1 rounded-xl"
                >
                  <PawPrint size={16} className="mr-2" />
                  Profilo
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
